import React, { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/DashboardComponents/PageHeader';
import Pagination from '../../components/Listing/Pagination';
import EmptyState from '../../components/Common/EmptyState';
import TruncatedText from '../../components/Common/TruncatedText';
import { ICONS } from '../../constants/icons';
import { get } from '../../api/axios';
import endpoints from '../../api/endpoints';
import { toastApiError } from '../../helpers/apiError';
import {
  XP_ACTIVITY_WITH,
  extractPaginatedList,
  mapXpActivity,
} from './rewardsData';

const PAGE_SIZE = 10;
const SKELETON_ROWS = 8;

const XpActivityListing = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [activities, setActivities] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [isLoading, setIsLoading] = useState(true);

  const fetchActivities = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await get(endpoints.getXpActivity, {
        params: {
          page: currentPage,
          per_page: PAGE_SIZE,
          sort_by: 'created_at',
          sort_direction: 'desc',
          'with[]': XP_ACTIVITY_WITH,
        },
      });

      if (response?.data?.success) {
        const page = extractPaginatedList(response);
        setActivities(page.list.map(mapXpActivity));
        setTotalItems(page.total);
        setPageSize(page.perPage);

        const lastPage = Number(page.lastPage);
        const totalPages = Math.max(
          1,
          Number.isFinite(lastPage) && lastPage > 0
            ? lastPage
            : Math.ceil((page.total || 0) / (page.perPage || PAGE_SIZE)) || 1
        );
        if (currentPage > totalPages) setCurrentPage(totalPages);
      } else if (response) {
        toastApiError(response, 'Failed to load XP activity');
        setActivities([]);
        setTotalItems(0);
      }
    } catch (err) {
      toastApiError(err, 'Failed to load XP activity');
      setActivities([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  return (
    <div className="page-content">
      <div className="rewards rewards--activity-listing">
        <PageHeader
          className="rewards__header"
          title="XP Activity"
          subtitle="All XP you've earned across projects and collaboration."
        />

        <div className="projects-listing__table-wrapper rewards__table-wrap">
          <div
            className="projects-listing__table rewards__table"
            role="table"
            aria-label="XP Activity"
            aria-busy={isLoading || undefined}
          >
            <div
              className="projects-listing__table-row projects-listing__table-row--head"
              role="row"
            >
              <div role="columnheader">Activity</div>
              <div role="columnheader">Date</div>
              <div role="columnheader">Time</div>
              <div role="columnheader">Earned XP</div>
            </div>

            {isLoading
              ? Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                  <div
                    key={`xp-activity-listing-skel-${index}`}
                    className="projects-listing__table-row"
                    role="row"
                    aria-hidden="true"
                  >
                    <div
                      className="projects-listing__table-cell projects-listing__table-cell--title"
                      role="cell"
                    >
                      <span className="projects-listing__table-title">
                        <span className="rewards__activity-icon">
                          <span className="amok-skeleton" />
                        </span>
                        <span className="amok-skeleton rewards__skel-line rewards__skel-line--activity" />
                      </span>
                    </div>
                    <div className="projects-listing__table-cell" role="cell">
                      <span className="amok-skeleton rewards__skel-line" />
                    </div>
                    <div className="projects-listing__table-cell" role="cell">
                      <span className="amok-skeleton rewards__skel-line rewards__skel-line--sm" />
                    </div>
                    <div
                      className="projects-listing__table-cell rewards__earned"
                      role="cell"
                    >
                      <span className="amok-skeleton rewards__skel-line rewards__skel-line--sm" />
                    </div>
                  </div>
                ))
              : null}

            {!isLoading
              ? activities.map(row => (
                  <div
                    key={row.id}
                    className="projects-listing__table-row"
                    role="row"
                  >
                    <div
                      className="projects-listing__table-cell projects-listing__table-cell--title"
                      role="cell"
                    >
                      <span className="projects-listing__table-title">
                        <span className="rewards__activity-icon" aria-hidden="true">
                          <img
                            src={ICONS.rewardsActivity}
                            alt=""
                            width={16}
                            height={16}
                          />
                        </span>
                        <TruncatedText
                          className="rewards__activity-name"
                          title={row.activity}
                        >
                          {row.activity}
                        </TruncatedText>
                      </span>
                    </div>
                    <div className="projects-listing__table-cell" role="cell">
                      <TruncatedText>{row.date}</TruncatedText>
                    </div>
                    <div className="projects-listing__table-cell" role="cell">
                      <TruncatedText>{row.time}</TruncatedText>
                    </div>
                    <div
                      className="projects-listing__table-cell rewards__earned"
                      role="cell"
                    >
                      +{row.earned}xp
                    </div>
                  </div>
                ))
              : null}
          </div>

          {!isLoading && activities.length === 0 ? (
            <EmptyState
              title="No XP activity yet"
              description="Earn XP by creating projects and collaborating."
            />
          ) : null}
        </div>

        <Pagination
          className="rewards__pagination"
          currentPage={currentPage}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          itemLabel="Activities"
          prevIconSrc={ICONS.chevronLeft}
          nextIconSrc={ICONS.chevronRight}
        />
      </div>
    </div>
  );
};

export default XpActivityListing;
